import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { workOrdersAPI, billingItemsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import WorkOrderForm from '../components/workorder/WorkOrderForm';
import {
  ClipboardList, Plus, CheckCircle2, XCircle, Clock, AlertTriangle,
  ExternalLink, Paperclip, RefreshCw, Wand2,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const STATUS_BADGE = {
  pending_review: { label: 'Awaiting review', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const money = (cents) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-fg-muted shrink-0">{label}</span>
    <span className="text-fg text-right break-words">{value || '—'}</span>
  </div>
);


/**
 * Rate lines on the pending job. Lines drafted from the manager's requested
 * bill rate are tagged, so Finance verifies rather than retypes; any
 * classification that couldn't be matched to a billing item is offered here
 * with a one-click add at the requested rate.
 */
const RateLinePanel = ({ detail, billingItems, canApprove, onChanged }) => {
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(null);
  const nameOf = (id) => billingItems.find((b) => b.id === id)?.name || id;
  const lines = detail.rate_lines || [];
  const gaps = detail.rate_line_gaps || [];

  const add = async (gap) => {
    const itemId = gap.suggested_billing_item_id || picks[gap.classification];
    if (!itemId) {
      toast.error(`Choose a billing item for ${gap.classification}`);
      return;
    }
    setBusy(gap.classification);
    try {
      await workOrdersAPI.addRateLine(detail.id, itemId, gap.bill_rate);
      toast.success(`Rate line added for ${gap.classification}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add that rate line');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-md border border-line p-3 space-y-2"
      data-testid="wo-rate-lines">
      <p className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
        Billing rate lines
      </p>

      {lines.length === 0 && (
        <p className="text-sm text-fg-muted">None yet.</p>
      )}
      {lines.map((l) => (
        <div key={l.id} className="flex items-center justify-between gap-2 text-sm"
          data-testid={`wo-rate-line-${l.id}`}>
          <span className="text-fg truncate flex items-center gap-1.5">
            {nameOf(l.billing_item_id)}
            {l.source === 'work_order' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-sunken
                text-fg-muted whitespace-nowrap" title="Drafted from the requested bill rate">
                from work order
              </span>
            )}
          </span>
          <span className="text-fg whitespace-nowrap">
            ${Number(l.bill_rate || 0).toFixed(2)}/{l.uom || 'Hour'}
          </span>
        </div>
      ))}

      {canApprove && gaps.length > 0 && (
        <div className="pt-2 border-t border-line-subtle space-y-2">
          <p className="text-xs text-fg-muted flex items-center gap-1.5">
            <Wand2 className="w-3.5 h-3.5" />
            Requested but not yet on the job
            {gaps[0]?.bill_rate ? ` — add at $${gaps[0].bill_rate.toFixed(2)}/hr` : ''}
          </p>
          {gaps.map((g) => (
            <div key={g.classification} className="flex items-center gap-2"
              data-testid={`wo-gap-${g.classification.replace(/[^a-z]+/gi, '-').toLowerCase()}`}>
              <span className="text-sm text-fg flex-1 truncate">{g.classification}</span>
              {g.suggested_billing_item_id ? (
                <span className="text-xs text-fg-muted truncate max-w-[9rem]">
                  {g.suggested_billing_item_name}
                </span>
              ) : (
                <Select value={picks[g.classification] || ''}
                  onValueChange={(v) => setPicks((p) => ({ ...p, [g.classification]: v }))}>
                  <SelectTrigger className="h-8 w-44"
                    data-testid={`wo-gap-select-${g.classification.replace(/[^a-z]+/gi, '-').toLowerCase()}`}>
                    <SelectValue placeholder="Pick billing item" />
                  </SelectTrigger>
                  <SelectContent>
                    {billingItems.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button size="sm" variant="outline" className="h-8 shrink-0"
                disabled={busy === g.classification} onClick={() => add(g)}
                data-testid={`wo-gap-add-${g.classification.replace(/[^a-z]+/gi, '-').toLowerCase()}`}>
                Add
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReviewDialog = ({ wo, open, onOpenChange, onDone, onCorrect }) => {
  const [detail, setDetail] = useState(null);
  const [billingItems, setBillingItems] = useState([]);
  const [comments, setComments] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!wo?.id) return;
    try {
      const res = await workOrdersAPI.get(wo.id);
      setDetail(res.data);
    } catch {
      toast.error('Could not load that work order');
    }
  }, [wo]);

  useEffect(() => { if (open) { setComments(''); setRejecting(false); load(); } }, [open, load]);

  useEffect(() => {
    if (!open || billingItems.length) return;
    billingItemsAPI.list({ is_active: true })
      .then((r) => setBillingItems(r.data || []))
      .catch(() => { /* names fall back to ids */ });
  }, [open, billingItems.length]);

  const act = async (kind) => {
    if (kind === 'reject' && comments.trim().length < 5) {
      toast.error('Say why it is being rejected — the manager needs to know what to fix');
      return;
    }
    setBusy(true);
    try {
      if (kind === 'approve') await workOrdersAPI.approve(wo.id, comments);
      else await workOrdersAPI.reject(wo.id, comments);
      toast.success(kind === 'approve' ? 'Work order approved' : 'Work order rejected');
      onDone();
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'That did not go through');
      load();
    } finally {
      setBusy(false);
    }
  };

  const readiness = detail?.readiness;
  const job = detail?.job;
  const client = detail?.client;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto scrollbar-thin"
        data-testid="work-order-review">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {detail?.wo_number || wo?.wo_number}
            {detail && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[detail.status]?.cls}`}>
                {STATUS_BADGE[detail.status]?.label}
              </span>
            )}
            {detail?.type === 'close' && <Badge variant="outline">Site closure</Badge>}
          </DialogTitle>
        </DialogHeader>

        {!detail ? (
          <div className="h-40 skeleton-pulse rounded" />
        ) : (
          <div className="space-y-4">
            {detail.status === 'rejected' && detail.review?.comments && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
                  Rejected by {detail.review.reviewed_by_name}
                </p>
                <p className="text-sm text-red-800 mt-1">
                  {detail.review.comments}
                </p>
              </div>
            )}

            {detail.status === 'pending_review' && readiness && (
              <div className={`rounded-md border p-3 ${readiness.ready
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-amber-300 bg-amber-50'}`}
                data-testid="work-order-readiness">
                <p className="text-sm font-semibold flex items-center gap-2">
                  {readiness.ready
                    ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" />Billing setup complete — ready to approve</>
                    : <><AlertTriangle className="w-4 h-4 text-amber-600" />Still needed before this can be approved</>}
                </p>
                {!readiness.ready && (
                  <ul className="mt-2 space-y-0.5">
                    {readiness.outstanding.map((o) => (
                      <li key={`${o.scope}-${o.field}`} className="text-sm text-fg-muted"
                        data-testid={`readiness-${o.field}`}>
                        • {o.label} <span className="text-xs">({o.scope})</span>
                      </li>
                    ))}
                  </ul>
                )}
                {!readiness.ready && (
                  <p className="text-xs text-fg-muted mt-2">
                    Fill these in on the Clients and Jobs screens, then come back here.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <div className="divide-y divide-line-subtle">
                <Row label="Raised by" value={detail.prepared_by_name} />
                <Row label="Submitted" value={formatDate(detail.submitted_at)} />
                <Row label="Client" value={client?.name} />
                <Row label="Customer no." value={client?.customer_number} />
                <Row label="Job number" value={job?.job_number} />
                <Row label="Site" value={job?.site_name} />
                <Row label="Address" value={[job?.site_address1, job?.site_city,
                  job?.site_state, job?.site_zip].filter(Boolean).join(', ')} />
                <Row label="Site contact" value={[job?.site_contact_first_name,
                  job?.site_contact_last_name].filter(Boolean).join(' ')} />
                <Row label="Site phone" value={(job?.site_phones || [])[0]?.phone} />
              </div>
              <div className="divide-y divide-line-subtle">
                <Row label="Company" value={job?.company} />
                <Row label="Classifications"
                  value={(detail.officer_classifications || []).join(', ')} />
                {'requested_pay_rate_cents' in detail && (
                  <Row label="Requested pay rate" value={money(detail.requested_pay_rate_cents)} />
                )}
                <Row label="Requested bill rate" value={money(detail.requested_bill_rate_cents)} />
                <Row label="Bill holidays" value={detail.holidays_billable ? 'Yes' : 'No'} />
                <Row label="Taxable" value={detail.taxable ? 'Yes' : 'No'} />
                <Row label="Net / terms" value={detail.net_term_days} />
                <Row label="Billing email" value={detail.billing_contact_email} />
              </div>
            </div>

            {detail.type !== 'close' && (
              <RateLinePanel detail={detail} billingItems={billingItems}
                canApprove={detail.can_approve && detail.status === 'pending_review'}
                onChanged={load} />
            )}

            {detail.additional_services && (
              <Row label="Additional services" value={detail.additional_services_detail} />
            )}
            {detail.notes && (
              <div>
                <p className="text-xs uppercase tracking-wide text-fg-muted">Notes</p>
                <p className="text-sm text-fg whitespace-pre-wrap mt-1">{detail.notes}</p>
              </div>
            )}
            {(detail.attachments || []).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-fg-muted mb-1">Attachments</p>
                {detail.attachments.map((a) => (
                  <a key={a.id} className="flex items-center gap-2 text-sm text-[hsl(var(--brand-accent))] hover:underline"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/work-orders/attachments/${a.id}/download`}
                    target="_blank" rel="noopener noreferrer" data-testid={`wo-attachment-${a.id}`}>
                    <Paperclip className="w-3.5 h-3.5" />{a.filename}
                  </a>
                ))}
              </div>
            )}

            {detail.status === 'pending_review' && detail.can_approve && (
              <div className="pt-2 border-t border-line">
                <p className="text-xs uppercase tracking-wide text-fg-muted mb-1">
                  {rejecting ? 'Why is this being rejected? (required)' : 'Comments (optional)'}
                </p>
                <Textarea rows={rejecting ? 3 : 2} value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={rejecting
                    ? 'Tell the manager exactly what to correct'
                    : 'Anything you want recorded with this decision'}
                  data-testid="wo-review-comments" />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {detail?.status === 'rejected' && onCorrect && (
            <Button onClick={() => { onOpenChange(false); onCorrect(detail); }}
              data-testid="wo-correct">
              Correct and resubmit
            </Button>
          )}
          {detail?.status === 'pending_review' && detail.can_approve && (
            <>
              {rejecting ? (
                <>
                  <Button variant="ghost" onClick={() => setRejecting(false)}>Back</Button>
                  <Button variant="destructive" disabled={busy} onClick={() => act('reject')}
                    data-testid="wo-confirm-reject">
                    <XCircle className="w-4 h-4 mr-1.5" />Confirm rejection
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setRejecting(true)}
                    data-testid="wo-reject">
                    <XCircle className="w-4 h-4 mr-1.5" />Reject
                  </Button>
                  <Button disabled={busy || !readiness?.ready} onClick={() => act('approve')}
                    data-testid="wo-approve">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" />Approve
                  </Button>
                </>
              )}
            </>
          )}
          {(!detail || detail.status === 'approved') && (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function WorkOrders() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([workOrdersAPI.meta(), workOrdersAPI.list()]);
      setMeta(m.data);
      setRows(l.data.work_orders || []);
    } catch {
      toast.error('Could not load work orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep link from a notification or the My Work card.
  useEffect(() => {
    const id = params.get('id');
    if (id && rows.length) {
      const hit = rows.find((r) => r.id === id);
      if (hit) setReviewing(hit);
    }
  }, [params, rows]);

  const buckets = useMemo(() => ({
    pending_review: rows.filter((r) => r.status === 'pending_review'),
    rejected: rows.filter((r) => r.status === 'rejected'),
    approved: rows.filter((r) => r.status === 'approved'),
  }), [rows]);

  const openCorrect = async (wo) => {
    try {
      const res = await workOrdersAPI.get(wo.id);
      setCorrecting(res.data);
      setFormOpen(true);
    } catch {
      toast.error('Could not open that work order');
    }
  };

  const List = ({ items, testId }) => (
    <div className="space-y-2" data-testid={testId}>
      {items.length === 0 && (
        <p className="text-sm text-fg-muted py-6">Nothing here right now.</p>
      )}
      {items.map((r) => (
        <button key={r.id} type="button"
          onClick={() => { setReviewing(r); setParams({ id: r.id }); }}
          className="stat-card w-full text-left flex items-start justify-between gap-3
            hover:border-[hsl(var(--brand-accent))]/50 transition-colors duration-150"
          data-testid={`wo-row-${r.wo_number}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-fg">{r.wo_number}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status]?.cls}`}>
                {STATUS_BADGE[r.status]?.label}
              </span>
              {r.type === 'close' && <Badge variant="outline" className="text-xs">Closure</Badge>}
            </div>
            <p className="text-sm text-fg mt-1 truncate">
              {r.site_name || r.new_client_name || r.client_name || 'Site'}
            </p>
            <p className="text-xs text-fg-muted mt-0.5">
              {r.client_name || r.new_client_name || '—'}
              {r.job_number ? ` · ${r.job_number}` : ''} · {r.prepared_by_name} ·{' '}
              {formatDate(r.submitted_at)}
            </p>
          </div>
          <ExternalLink className="w-4 h-4 text-fg-muted shrink-0 mt-1" />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return <div className="space-y-4" data-testid="work-orders-loading">
      <div className="h-9 w-56 skeleton-pulse rounded" />
      <div className="h-32 skeleton-pulse rounded-lg" />
    </div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="work-orders-page">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[hsl(var(--brand-accent))]" aria-hidden="true" />
            Work Orders
          </h1>
          <p className="text-sm text-fg-muted mt-1">
            {meta?.can_approve
              ? 'Review what managers have raised, complete the billing setup, then approve.'
              : 'Raise a work order to stand up a new site. Finance reviews it before it goes live.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} data-testid="work-orders-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
          </Button>
          {meta?.can_submit && (
            <Button onClick={() => { setCorrecting(null); setFormOpen(true); }}
              data-testid="work-orders-new">
              <Plus className="w-4 h-4 mr-1.5" />New work order
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="pending_review">
        <TabsList>
          <TabsTrigger value="pending_review" data-testid="wo-tab-pending">
            <Clock className="w-3.5 h-3.5 mr-1.5" />
            Awaiting review ({buckets.pending_review.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="wo-tab-rejected">
            Rejected ({buckets.rejected.length})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="wo-tab-approved">
            Approved ({buckets.approved.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pending_review" className="mt-4">
          <List items={buckets.pending_review} testId="wo-list-pending" />
        </TabsContent>
        <TabsContent value="rejected" className="mt-4">
          <List items={buckets.rejected} testId="wo-list-rejected" />
        </TabsContent>
        <TabsContent value="approved" className="mt-4">
          <List items={buckets.approved} testId="wo-list-approved" />
        </TabsContent>
      </Tabs>

      <WorkOrderForm open={formOpen} onOpenChange={(v) => {
        setFormOpen(v);
        if (!v) setCorrecting(null);
      }} meta={meta} existing={correcting} onSaved={load} />

      <ReviewDialog wo={reviewing} open={!!reviewing}
        onOpenChange={(v) => { if (!v) { setReviewing(null); setParams({}); } }}
        onDone={load} onCorrect={openCorrect} />
    </div>
  );
}
